import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flower, LayoutGrid, Star, TriangleAlert } from 'lucide-react';
import { useRequireResult } from '../hooks/useRouteGuards';
import { useAppStore } from '../store/useAppStore';
import type { ClusterId } from '../types/ids';
import { ScoreCircle } from '../components/result/ScoreCircle';
import { ClusterBluete } from '../components/result/ClusterBluete';
import { ClusterCard } from '../components/result/ClusterCard';
import { ClusterDetailSection } from '../components/result/ClusterDetailSection';
import { ClusterDetailModal } from '../components/result/ClusterDetailModal';
import { SectionHeading } from '../components/result/SectionHeading';
import { TensionsSection } from '../components/result/TensionsSection';
import { InsightList } from '../components/result/InsightList';
import { ChannelRoleSection } from '../components/result/ChannelRoleSection';
import { VisibilityModelSection } from '../components/result/VisibilityModelSection';
import { ActionPlanSection } from '../components/result/ActionPlanSection';
import { Button } from '../components/ui/Button';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { downloadResultPdf } from '../pdf/generateResultPdf';
import { trackEvent } from '../utils/posthogClient';

export function ResultPage() {
  const ready = useRequireResult();
  const navigate = useNavigate();
  const session = useAppStore((state) => state.session);
  const dataModel = useAppStore((state) => state.dataModel);
  const discardSession = useAppStore((state) => state.discardSession);
  const startNewSession = useAppStore((state) => state.startNewSession);
  const showWeightingDetails = useAppStore(
    (state) => state.settings.featureFlags.weightingTransparency.resultClusterDetails,
  );
  const showClusterDetailSection = useAppStore(
    (state) => state.settings.featureFlags.resultPage.clusterDetailSection,
  );
  const clusterDetailMode = useAppStore(
    (state) => state.settings.featureFlags.resultPage.clusterDetailMode,
  );
  const showPdfExportButton = useAppStore(
    (state) => state.settings.featureFlags.resultPage.showPdfExportButton,
  );
  const pdfExportMode = useAppStore(
    (state) => state.settings.featureFlags.resultPage.pdfExportMode,
  );
  const showClusterBluete = useAppStore(
    (state) => state.settings.featureFlags.resultPage.showClusterBluete,
  );
  const clusterBlueteMode = useAppStore(
    (state) => state.settings.featureFlags.resultPage.clusterBlueteMode,
  );
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);
  const identity = useAppStore((state) => state.identity);
  const [confirmRestart, setConfirmRestart] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [activeDetailClusterId, setActiveDetailClusterId] = useState<ClusterId | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const trackedResultIdRef = useRef<string | null>(null);

  const detailsAction: 'anchor' | 'modal' | 'hidden' = !showClusterDetailSection
    ? 'hidden'
    : clusterDetailMode === 'modal'
      ? 'modal'
      : 'anchor';

  const result = session?.result;

  useEffect(() => {
    if (!analyticsEnabled || !result || trackedResultIdRef.current === result.resultId) return;
    trackedResultIdRef.current = result.resultId;
    trackEvent('result_shown', {
      overall_score: result.overall.score,
      overall_percentage: result.overall.percentage,
      overall_band: result.overall.band,
      cluster_percentages: Object.fromEntries(
        result.clusters.map((cluster) => [cluster.clusterId, cluster.percentage]),
      ),
      tensions_count: result.tensions.length,
      recommended_channel_role: result.recommendedChannelRole.role,
      recommended_visibility_model: result.recommendedVisibilityModel.model,
      schema_version: result.schemaVersion,
      scoring_version: result.scoringVersion,
      ...(identity.email ? { email: identity.email } : {}),
      ...(identity.uid ? { uid: identity.uid } : {}),
    });
  }, [analyticsEnabled, result, identity]);

  if (!ready || !result) return null;

  function handleRestart(): void {
    if (analyticsEnabled) trackEvent('test_restart_confirmed', { source: 'result_page' });
    discardSession();
    setConfirmRestart(false);
    startNewSession();
    void navigate('/vorbereitung');
  }

  function handleDelete(): void {
    if (analyticsEnabled) trackEvent('local_data_deleted');
    discardSession();
    setConfirmDelete(false);
    void navigate('/');
  }

  async function handleExportPdf(): Promise<void> {
    if (!result) return;
    if (analyticsEnabled) trackEvent('pdf_export_click', { mode: pdfExportMode });

    if (pdfExportMode === 'browserPrint') {
      window.print();
      return;
    }

    setIsGeneratingPdf(true);
    try {
      await downloadResultPdf({
        result,
        recommendations: dataModel.recommendationRules.recommendations,
        questions: dataModel.questions,
        maximumFeaturedTensions: dataModel.appConfig.tensions.maximumFeaturedCards,
        showClusterDetailSection,
        showWeightingDetails,
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  }

  return (
    <div className="page-container stack" id="result-print-area">
      <section className="stack" style={{ alignItems: 'center', textAlign: 'center' }}>
        <ScoreCircle
          score={result.overall.score}
          maximum={result.overall.maximum}
          percentage={result.overall.percentage}
          statusLabel={result.overall.label}
        />
        <h1>{result.summary.headline}</h1>
        <p>{result.summary.summaryText}</p>
      </section>

      <section className="card card--muted">
        <p>
          <strong>Wichtigste Voraussetzung:</strong> {result.summary.mainCondition}
        </p>
        {result.summary.mainTension && (
          <p>
            <strong>Wichtigstes Spannungsfeld:</strong> {result.summary.mainTension}
          </p>
        )}
      </section>

      {showClusterBluete && (
        <section className="stack">
          <SectionHeading icon={Flower}>Deine Bewertungsbereiche als Blüte</SectionHeading>
          <ClusterBluete clusters={result.clusters} mode={clusterBlueteMode} />
        </section>
      )}

      <section className="stack">
        <SectionHeading icon={LayoutGrid}>Deine sechs Bewertungsbereiche</SectionHeading>
        <div className="cluster-grid">
          {result.clusters.map((cluster) => (
            <ClusterCard
              key={cluster.clusterId}
              cluster={cluster}
              detailsAction={detailsAction}
              onOpenDetails={(clusterId) => {
                if (analyticsEnabled) {
                  trackEvent('cluster_details_opened', {
                    cluster_id: clusterId,
                    mode: detailsAction === 'modal' ? 'modal' : 'anchor',
                  });
                }
                if (detailsAction === 'modal') setActiveDetailClusterId(clusterId);
              }}
            />
          ))}
        </div>
      </section>

      <TensionsSection
        tensions={result.tensions}
        maximumFeatured={dataModel.appConfig.tensions.maximumFeaturedCards}
        recommendations={dataModel.recommendationRules.recommendations}
      />

      <InsightList
        title="Stärken"
        icon={Star}
        insights={result.strengths}
        emptyText="Aktuell wurden keine besonders herausragenden Einzelstärken erkannt."
      />

      <InsightList
        title="Wichtigste Engpässe"
        icon={TriangleAlert}
        insights={result.weakPoints}
        emptyText="Aktuell wurden keine besonders kritischen Einzel-Engpässe erkannt."
      />

      <ChannelRoleSection channelRole={result.recommendedChannelRole} />
      <VisibilityModelSection visibilityModel={result.recommendedVisibilityModel} />

      {detailsAction === 'anchor' && (
        <ClusterDetailSection
          clusters={result.clusters}
          questions={dataModel.questions}
          showWeightingDetails={showWeightingDetails}
        />
      )}

      <ActionPlanSection
        actionPlan={result.actionPlan}
        catalog={dataModel.recommendationRules.recommendations}
      />

      <section className="card card--muted">
        <p>
          Dieses Ergebnis bewertet die strukturelle Eignung von Instagram für dein Angebot. Es ist
          keine Erfolgsgarantie und keine Bewertung deiner Person.
        </p>
      </section>

      <section className="stack no-print" style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {showPdfExportButton && (
          <Button onClick={() => void handleExportPdf()} disabled={isGeneratingPdf}>
            {pdfExportMode === 'reactPdf'
              ? isGeneratingPdf
                ? 'PDF wird erstellt…'
                : 'PDF herunterladen'
              : 'Drucken / als PDF speichern'}
          </Button>
        )}
        <Button variant="secondary" onClick={() => setConfirmRestart(true)}>
          Test neu starten
        </Button>
        <Button variant="danger" onClick={() => setConfirmDelete(true)}>
          Lokale Daten löschen
        </Button>
      </section>

      {detailsAction === 'modal' && (
        <ClusterDetailModal
          cluster={result.clusters.find((c) => c.clusterId === activeDetailClusterId) ?? null}
          questions={dataModel.questions}
          showWeightingDetails={showWeightingDetails}
          onClose={() => setActiveDetailClusterId(null)}
        />
      )}

      <ConfirmDialog
        open={confirmRestart}
        title="Test neu starten?"
        description="Dein aktuelles Ergebnis und deine Antworten werden dabei durch eine neue, leere Sitzung ersetzt."
        confirmLabel="Neu starten"
        onConfirm={handleRestart}
        onCancel={() => setConfirmRestart(false)}
        danger
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Lokale Daten löschen?"
        description="Alle gespeicherten Antworten und dein Ergebnis werden aus diesem Browser entfernt."
        confirmLabel="Löschen"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
        danger
      />
    </div>
  );
}
